import React, { useRef } from 'react';

export interface FileItem {
  name: string;
  path: string;
  type: 'directory' | 'notebook' | 'file';
  size?: number;
  created?: string;
  modified?: string;
  content?: any;
}

// Chevron for directories — defined at module level to avoid recreation on every render
export const DirChevron = React.memo(({ isExpanded, isLoading }: { isExpanded: boolean; isLoading: boolean }) => {
  if (isLoading) {
    return (
      <svg className="w-3 h-3 animate-spin text-gray-500 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  }
  return (
    <svg
      className={`w-3 h-3 mr-1 flex-shrink-0 text-gray-500 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
      fill="currentColor" viewBox="0 0 20 20"
    >
      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  );
});

DirChevron.displayName = 'DirChevron';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type: string, name: string, isExpanded?: boolean): React.ReactNode {
  if (type === 'directory') {
    if (isExpanded) {
      return (
        <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a3 3 0 00-3 3v1.5a1.5 1.5 0 01-3 0V6z" clipRule="evenodd" />
          <path d="M6 12a2 2 0 012-2h8a2 2 0 012 2v2a2 2 0 01-2 2H2h2a2 2 0 002-2v-2z" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
      </svg>
    );
  } else if (type === 'notebook' || name.endsWith('.ipynb')) {
    return (
      <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  } else if (name.endsWith('.py')) {
    return (
      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

export interface FileTreeRowProps {
  file: FileItem;
  depth: number;
  isSelected: boolean;
  isMultiSelected: boolean;
  isExpanded: boolean;
  isDirLoading: boolean;
  isDragged: boolean;
  isDropTarget: boolean;
  isInvalidDropTarget: boolean;
  isRenaming: boolean;
  renameValue: string;
  deleteConfirm: boolean;
  searchQuery?: string;
  onFileClick: (file: FileItem, e: React.MouseEvent) => void;
  onDoubleClick: (file: FileItem) => void;
  onContextMenu: (e: React.MouseEvent, file: FileItem) => void;
  onDragStart: (e: React.DragEvent, file: FileItem) => void;
  onDragEnd: () => void;
  onDragOver?: (e: React.DragEvent, path: string) => void;
  onDragLeave?: (e: React.DragEvent, path: string) => void;
  onDrop?: (e: React.DragEvent, path: string) => void;
  onRenameChange: (value: string) => void;
  onRenameSubmit: (file: FileItem) => void;
  onRenameCancel: () => void;
  onDeleteConfirm: (file: FileItem) => void;
  onDeleteCancel: () => void;
  renameInitTimestamp: number;
}

function highlightMatch(text: string, query?: string): React.ReactNode {
  if (!query) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerText.indexOf(lowerQuery);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-500/30 text-yellow-200 rounded-sm px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export const FileTreeRow = React.memo<FileTreeRowProps>(({
  file, depth, isSelected, isMultiSelected, isExpanded, isDirLoading,
  isDragged, isDropTarget, isInvalidDropTarget, isRenaming, renameValue,
  deleteConfirm, searchQuery, onFileClick, onDoubleClick, onContextMenu, onDragStart,
  onDragEnd, onDragOver, onDragLeave, onDrop, onRenameChange,
  onRenameSubmit, onRenameCancel, onDeleteConfirm, onDeleteCancel,
  renameInitTimestamp,
}) => {
  const isDir = file.type === 'directory';
  const renameInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <button
        data-path={file.path}
        draggable={true}
        onDragStart={(e) => onDragStart(e, file)}
        onDragEnd={onDragEnd}
        {...(isDir ? {
          onDragOver: (e: React.DragEvent<HTMLButtonElement>) => onDragOver?.(e, file.path),
          onDragLeave: (e: React.DragEvent<HTMLButtonElement>) => onDragLeave?.(e, file.path),
          onDrop: (e: React.DragEvent<HTMLButtonElement>) => onDrop?.(e, file.path),
        } : {})}
        onClick={(e) => onFileClick(file, e)}
        onDoubleClick={() => onDoubleClick(file)}
        onContextMenu={(e) => onContextMenu(e, file)}
        className={`w-full flex items-center py-1.5 text-left transition-colors border-l-2 ${
          isDragged
            ? 'opacity-50 text-gray-500 border-transparent'
            : isDropTarget
              ? 'bg-blue-500/10 text-white border-blue-500 ring-1 ring-blue-500/50'
              : isInvalidDropTarget
                ? 'cursor-not-allowed border-transparent text-gray-300'
                : isMultiSelected
                  ? 'bg-blue-500/15 text-white border-blue-400/70'
                  : isSelected
                    ? 'bg-blue-500/10 text-white border-blue-500 shadow-lg shadow-blue-500/10'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white border-transparent'
        }`}
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
      >
        {isDir && (
          <DirChevron isExpanded={isExpanded} isLoading={isDirLoading} />
        )}
        {!isDir && (
          <span className="w-3 mr-1 flex-shrink-0" />
        )}
        <span className="mr-2 flex-shrink-0">
          {getFileIcon(file.type, file.name, isExpanded)}
        </span>
        {isRenaming ? (
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRenameSubmit(file);
              if (e.key === 'Escape') onRenameCancel();
              e.stopPropagation();
            }}
            onBlur={() => {
              if (Date.now() - renameInitTimestamp < 200) return;
              onRenameSubmit(file);
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 text-sm bg-gray-800 text-white border border-blue-500/50 rounded px-1.5 py-0.5 outline-none min-w-0"
            autoFocus
          />
        ) : (
          <span className="flex-1 text-sm truncate">{highlightMatch(file.name, searchQuery)}</span>
        )}
        {!isDir && file.size && !isRenaming && (
          <span className="text-xs text-gray-500 ml-2">
            {formatFileSize(file.size)}
          </span>
        )}
      </button>

      {deleteConfirm && (
        <div
          className="flex items-center gap-2 py-1.5 bg-red-900/20 border-l-2 border-red-500"
          style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
        >
          <span className="text-xs text-red-300 flex-1 truncate">
            Delete &ldquo;{file.name}&rdquo;?
          </span>
          <button
            onClick={() => onDeleteConfirm(file)}
            className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-500 transition-colors"
          >
            Delete
          </button>
          <button
            onClick={() => onDeleteCancel()}
            className="px-2 py-0.5 text-xs text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}, (prev, next) => {
  return prev.file.path === next.file.path
    && prev.file.name === next.file.name
    && prev.isSelected === next.isSelected
    && prev.isMultiSelected === next.isMultiSelected
    && prev.isExpanded === next.isExpanded
    && prev.isDirLoading === next.isDirLoading
    && prev.isDragged === next.isDragged
    && prev.isDropTarget === next.isDropTarget
    && prev.isInvalidDropTarget === next.isInvalidDropTarget
    && prev.isRenaming === next.isRenaming
    && prev.renameValue === next.renameValue
    && prev.deleteConfirm === next.deleteConfirm
    && prev.searchQuery === next.searchQuery;
});

FileTreeRow.displayName = 'FileTreeRow';
